import { useState } from "react";
import { Dialog, DialogContent, Button, TextField, Popover, Card, Link, IconButton, Badge } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import { pluralize } from "helpers/coll";
import { FormSection, renderFormAssigneeType } from "Backend";
import { AssignFormSectionsDocument, FormAssigneeType, NamedUser } from "generated/graphql";
import { fullNameFromParts } from "helpers/name-helpers";
import { usePopover } from "helpers/hooks/usePopover";
import UserAutoComplete from "components/UserAutocomplete";
import { AddCircle, FilterList, RemoveCircle } from "@mui/icons-material";
import CheckboxList from "components/CheckboxList";
import CaseFilters from "SBR/admin/CaseFilters";
import { UseCases } from "./useCases";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectFormId, selectFormSections } from "redux/slices/form.slice";
import { fetchClientLensCases, selectClientLensAssignees } from "redux/slices/clientLens";
import { Number1, Number2, Number3 } from "components/Icons";
import { enqueueSnackbar } from "notistack";
import { extractGQLError } from "shared/PatternGraphQLError";
import CheckboxRadioGroup from "components/CheckboxRadioGroup";
import { LoadingButton } from "@mui/lab";
import { runGqlMutation } from "gqlClient";

type Assignee = NamedUser & {
  cases: number;
};

enum ModalStage {
  SECTIONS,
  ASSIGNEE_TYPE,
  ASSIGNEES,
  CONTINUE,
}

const sectionHeaderClasses =
  "text-sm border border-solid border-1 border-gray-100 rounded-lg py-2 px-3 flex gap-1 items-center mt-4";

const assigneeActionClasses = "text-xs text-black flex gap-1 items-center cursor-pointer";

const assigneeGridClasses = "grid grid-cols-4 gap-1 items-center m-2";

const AddAssigneeRow = ({
  disabled,
  clientLensAssignees,
  numAssigned,
  numCases,
  addAssignee,
}: {
  disabled: boolean;
  clientLensAssignees: NamedUser[];
  numAssigned: number;
  numCases: number;
  addAssignee: (user: NamedUser) => void;
}) => {
  const popover = usePopover();
  return (
    <>
      <Popover open={popover.isOpen} anchorEl={popover.anchorEl} onClose={popover.onPopoverClose}>
        <Card className="p-4 pb-6 min-w-80">
          <UserAutoComplete
            label="Search"
            variant="standard"
            textFieldProps={{
              autoFocus: true,
            }}
            options={clientLensAssignees}
            selected={undefined}
            onChange={(newAssignee) => {
              if (newAssignee !== undefined) {
                addAssignee(newAssignee);
                popover.onPopoverClose();
              }
            }}
          />
        </Card>
      </Popover>
      <div className={assigneeGridClasses}>
        <div className="col-1">
          <Link
            className={assigneeActionClasses}
            onClick={(evt) => {
              if (!disabled) {
                evt.stopPropagation();
                popover.onPopoverOpen(evt.currentTarget);
              }
            }}>
            <AddCircle /> add assignee
          </Link>
        </div>
        <div className="col-1 text-xs">
          Total selected: {numAssigned}/{numCases}
        </div>
      </div>
    </>
  );
};

const AssigneeRow = ({
  disabled,
  assignee,
  numCases,
  setAssigneeCases,
  removeAssignee,
}: {
  disabled: boolean;
  assignee: Assignee;
  numCases: number;
  setAssigneeCases: (id: number, n: number) => void;
  removeAssignee: (id: number) => void;
}) => {
  return (
    <div className={assigneeGridClasses}>
      <div className="col-1 text-xs">{fullNameFromParts(assignee.firstName, assignee.lastName)}</div>
      <div className="col-1 text-xs">
        <div className="flex items-center gap-1">
          <TextField
            disabled={disabled}
            label="cases"
            type="number"
            size="small"
            value={assignee.cases}
            onChange={(evt) => setAssigneeCases(assignee.id, parseInt(evt.target.value))}
          />{" "}
          ({Math.round((assignee.cases / numCases) * 100)}%)
        </div>
      </div>
      <div className="col-1">
        <Link
          className={assigneeActionClasses}
          onClick={() => {
            if (!disabled) {
              removeAssignee(assignee.id);
            }
          }}>
          <RemoveCircle /> remove assignee
        </Link>
      </div>
    </div>
  );
};

export function BulkAssignCasesModal({
  open,
  setOpen,
  lensId,
  casesHook,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  lensId: number;
  casesHook: UseCases;
}) {
  const dispatch = useAppDispatch();
  const formId = useAppSelector(selectFormId);
  const clientLensAssignees = useAppSelector(selectClientLensAssignees);
  const formSections = useAppSelector(selectFormSections);
  const filterPopover = usePopover();
  const [sectionIncluded, setSectionIncluded] = useState<FormSection[]>([]);
  const [assigneeType, setAssigneeType] = useState<FormAssigneeType | undefined>(undefined);
  const [{ manuallyAssigned, assignees }, setAssignees] = useState<{
    manuallyAssigned: boolean;
    assignees: Map<number, Assignee>;
  }>({ manuallyAssigned: false, assignees: new Map() });
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const handleCancel = () => {
    if (!isAssigning) {
      setOpen(false);
    }
  };

  const handleConfirmationCancel = () => {
    if (!isAssigning) {
      setConfirmationOpen(false);
    }
  };

  const handleConfirmation = async () => {
    setIsAssigning(true);
    if (formId !== undefined) {
      await runGqlMutation(AssignFormSectionsDocument, {
        formId,
        sectionIds: sectionIncluded.map((section) => section.id),
        assigneeType: assigneeType || FormAssigneeType.Review,
        assigneeShares: Array.from(assignees, ([id, assignee]) => ({ userId: id, cases: assignee.cases })),
        filters: casesHook.getCasesParamsInput,
      })
        .then(async () => {
          enqueueSnackbar("Form sections assigned", { variant: "info" });
          await dispatch(fetchClientLensCases(casesHook.getLensCasesQueryVariables));
        })
        .catch((err) => {
          const gql = extractGQLError(err);
          enqueueSnackbar(`Failed to assign form sections: ${gql?.message ?? "unknown"}`, { variant: "error" });
        });
    }
    setIsAssigning(false);
    setConfirmationOpen(false);
    setOpen(false);
  };

  const numAssigned = Array.from(assignees.values(), (assignee) => assignee.cases).reduce((prev, cur) => prev + cur, 0);

  const modalStage = (() => {
    if (sectionIncluded.length === 0) {
      return ModalStage.SECTIONS;
    }
    if (assigneeType === undefined) {
      return ModalStage.ASSIGNEE_TYPE;
    }
    if (numAssigned !== casesHook.numCases) {
      return ModalStage.ASSIGNEES;
    }
    return ModalStage.CONTINUE;
  })();
  const assigneeTypeDisabled = modalStage < ModalStage.ASSIGNEE_TYPE;
  const assigneesDisabled = modalStage < ModalStage.ASSIGNEES;
  const continueDisabled = modalStage < ModalStage.CONTINUE;

  const redistribute = (as: Map<number, Assignee>) => {
    const newAs = new Map(as);
    const quot = Math.floor(casesHook.numCases / as.size);
    let rem = casesHook.numCases % assignees.size;
    for (const k of newAs.keys()) {
      const cases = quot + (rem > 0 ? 1 : 0);
      rem -= 1;
      const existing = newAs.get(k);
      if (existing) {
        newAs.set(k, { ...existing, cases });
      }
    }
    return newAs;
  };

  const addAssignee = (assignee: NamedUser) => {
    if (!assigneesDisabled) {
      const as = new Map(assignees);
      as.set(assignee.id, { ...assignee, cases: 0 });
      if (!manuallyAssigned) {
        setAssignees({ manuallyAssigned, assignees: redistribute(as) });
      } else {
        setAssignees({ manuallyAssigned, assignees: as });
      }
    }
  };

  const removeAssignee = (id: number) => {
    if (!assigneesDisabled) {
      const as = new Map(assignees);
      as.delete(id);
      if (!manuallyAssigned) {
        setAssignees({ manuallyAssigned, assignees: redistribute(as) });
      } else {
        setAssignees({ manuallyAssigned, assignees: as });
      }
    }
  };

  const setAssigneeCases = (id: number, cases: number) => {
    if (!assigneesDisabled) {
      const as = new Map(assignees);
      const existing = as.get(id);
      if (existing) {
        as.set(id, { ...existing, cases });
      }
      setAssignees({ manuallyAssigned: true, assignees: as });
    }
  };

  const distributeEvenly = () => {
    setAssignees({ manuallyAssigned: false, assignees: redistribute(assignees) });
  };

  return (
    <>
      <Dialog open={confirmationOpen} onClose={handleConfirmationCancel} fullWidth maxWidth="xs">
        <DialogContent>
          <div className="flex flex-col gap-2">
            <p className="font-bold">Confirm Assignments</p>
            <p className="text-sm">
              You are about to assign {renderFormAssigneeType(assigneeType || FormAssigneeType.Review)} Assignees to{" "}
              {casesHook.numCases} {pluralize(casesHook.numCases, "case")}. This will update the following sections:
            </p>
            {Array.from(assignees, ([id, assignee]) => (
              <div key={id} className="text-sm">
                {"\u2022"} {fullNameFromParts(assignee.firstName, assignee.lastName)} - {assignee.cases} (
                {sectionIncluded.map((section) => section.name).join(", ")})
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button disabled={isAssigning} onClick={handleConfirmationCancel} color="default">
            Cancel
          </Button>
          <LoadingButton loading={isAssigning} onClick={handleConfirmation} variant="outlined" color="default">
            Submit Assignments
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogContent>
          <p className="font-bold">Case Assignment</p>
          <div className="flex items-center gap-2">
            <p className="text-xs font-bold text-gray-400">
              Applied Filters
              <IconButton onClick={(evt) => filterPopover.onPopoverOpen(evt.currentTarget)}>
                <Badge showZero={false} badgeContent={casesHook.filtersApplied}>
                  <FilterList />
                </Badge>
              </IconButton>
              <CaseFilters
                filterPopover={filterPopover}
                lensId={lensId}
                staticAssignees={[]}
                casesHook={casesHook}
                readonly={true}
              />
            </p>
            <p className="text-xs">
              {pluralize(casesHook.numCases, "Case")} selected: {casesHook.numCases}
            </p>
          </div>
          <div className={sectionHeaderClasses}>
            <Number1 /> Choose Sections
          </div>
          <div className="flex items-center gap-2 p-1">
            <Button
              size="small"
              variant="text"
              onClick={() => setSectionIncluded(formSections)}
              className="pointer-events-auto">
              Select All
            </Button>
            <Button size="small" variant="text" onClick={() => setSectionIncluded([])} className="pointer-events-auto">
              Deselect All
            </Button>
          </div>
          <div className="flex flex-col">
            <CheckboxList
              allItems={formSections}
              itemKey={(section) => section.id.toString()}
              itemName={(section) => section.name}
              onChange={(ss) => setSectionIncluded(ss)}
              selected={sectionIncluded}
            />
          </div>
          <div className={assigneeTypeDisabled ? "opacity-50" : ""}>
            <div className={sectionHeaderClasses}>
              <Number2 /> Choose Assignee Type
            </div>
            <div className="px-3">
              <CheckboxRadioGroup
                disabled={assigneeTypeDisabled}
                options={[FormAssigneeType.Review, FormAssigneeType.Qa]}
                value={assigneeType}
                onChange={setAssigneeType}
                renderOption={renderFormAssigneeType}
                row
              />
            </div>
          </div>
          <div className={assigneesDisabled ? "opacity-50" : ""}>
            <div className={sectionHeaderClasses}>
              <Number3 /> Choose Assignees
            </div>
            {Array.from(assignees, ([id, assignee]) => (
              <AssigneeRow
                disabled={assigneesDisabled}
                key={id}
                assignee={assignee}
                numCases={casesHook.numCases}
                setAssigneeCases={setAssigneeCases}
                removeAssignee={removeAssignee}
              />
            ))}
            <AddAssigneeRow
              disabled={assigneesDisabled}
              clientLensAssignees={clientLensAssignees}
              numAssigned={numAssigned}
              numCases={casesHook.numCases}
              addAssignee={addAssignee}
            />
            {assignees.size > 0 && (
              <div className={assigneeGridClasses}>
                <div className="col-1 col-start-2">
                  <Link className={assigneeActionClasses} onClick={distributeEvenly}>
                    Distribute Evenly
                  </Link>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button disabled={isAssigning} onClick={handleCancel} color="default">
            Cancel
          </Button>
          <LoadingButton
            loading={isAssigning}
            onClick={() => setConfirmationOpen(true)}
            variant="outlined"
            color="default"
            disabled={continueDisabled}>
            Continue
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
